import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  IonContent,
  IonHeader,
  IonIcon,
  IonToolbar
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { menuOutline } from 'ionicons/icons';
import { filter, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService, User } from '../../services/auth.service';
import { PlatformModeService } from '../../services/platform-mode.service';
import { AnalyticsService } from '../../services/analytics.service';
import { UserMenuComponent } from '../../components/user-menu/user-menu.component';
import { AppContextMenuComponent } from '../../components/app-context-menu/app-context-menu.component';
import { AiAssistantComponent } from '../../components/ai-assistant/ai-assistant.component';
import { AssistantDisplayMode, AssistantLayoutState, AssistantUiStateService } from '../../services/assistant-ui-state.service';
import { AppShellPageConfig, DEFAULT_APP_SHELL_CONFIG } from './app-shell.models';
import { AppSidebarComponent } from './app-sidebar.component';
import { AppRightPanelComponent } from './app-right-panel.component';
import { TranslatePipe } from '../../pipes/translate.pipe';

type AssistantResizeDirection = 'side' | 'bottom';

@Component({
  selector: 'app-app-shell',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    IonContent,
    IonHeader,
    IonIcon,
    IonToolbar,
    TranslatePipe,
    UserMenuComponent,
    AppContextMenuComponent,
    AiAssistantComponent,
    AppSidebarComponent,
    AppRightPanelComponent
  ],
  templateUrl: './app-shell.component.html',
  styleUrls: ['./app-shell.component.scss']
})
export class AppShellComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly desktopSidebarPinStorageKey = 'app-shell-sidebar-pin-mode';
  private readonly desktopSidebarVisibilityStorageKey = 'app-shell-sidebar-visibility';
  private readonly rightPanelStateStoragePrefix = 'bw-app-shell-right-panel-open';
  private sidebarHoverExpandTimer: ReturnType<typeof setTimeout> | null = null;
  private assistantResizeCleanup: (() => void) | null = null;

  currentUser: User | null = null;
  currentConfig: AppShellPageConfig = DEFAULT_APP_SHELL_CONFIG;
  desktopSidebarPinnedExpanded = true;
  desktopSidebarHoverExpanded = false;
  desktopSidebarVisible = true;
  mobileSidebarOpen = false;
  rightPanelOpen = false;
  isAiChatRoute = false;
  assistantLayout: AssistantLayoutState = { displayMode: 'floating', isOpen: false, sidePanelWidth: 448, bottomPanelHeight: 352 };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly analyticsService: AnalyticsService,
    private readonly assistantUiStateService: AssistantUiStateService,
    public readonly platformModeService: PlatformModeService
  ) {
    addIcons({ menuOutline });
    this.destroyRef.onDestroy(() => {
      this.clearSidebarHoverExpandTimer();
      this.clearAssistantResize();
    });
  }

  ngOnInit(): void {
    this.restoreDesktopSidebarPinMode();
    this.restoreDesktopSidebarVisibility();
    this.restoreAssistantLayoutFromUrl();

    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.currentUser = user;
        this.analyticsService.start(user?.productAnalyticsEnabled ?? false);
      });

    this.assistantUiStateService.layoutState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((layout) => {
        this.assistantLayout = layout;
        this.syncAssistantLayoutToUrl(layout);
        this.changeDetectorRef.markForCheck();
      });

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const previousSupportsRightPanel = this.routeHasRightPanel;
        const previousRightPanelOpen = this.rightPanelOpen;
        const previousRightPanelStorageKey = this.resolveRightPanelStorageKey();
        this.mobileSidebarOpen = false;
        this.currentConfig = this.resolveShellConfig();
        this.isAiChatRoute = this.router.url.split('?')[0] === '/ai/chat';
        this.syncAssistantLayoutToUrl(this.assistantLayout);
        if (!this.routeHasRightPanel) {
          this.rightPanelOpen = false;
          return;
        }

        if (this.isCompactMode) {
          this.rightPanelOpen = false;
          return;
        }

        const currentRightPanelStorageKey = this.resolveRightPanelStorageKey();
        if (!previousSupportsRightPanel || previousRightPanelStorageKey !== currentRightPanelStorageKey) {
          this.rightPanelOpen = this.restoreRightPanelOpenState();
          return;
        }

        this.rightPanelOpen = previousRightPanelOpen;
      });
  }

  get routeHasRightPanel(): boolean {
    return !!this.currentConfig.rightPanel;
  }

  get showRightPanel(): boolean {
    return this.routeHasRightPanel && this.rightPanelOpen;
  }

  get isCompactMode(): boolean {
    return this.platformModeService.isCompactViewport();
  }

  get isSidebarCollapsed(): boolean {
    if (this.isCompactMode) {
      return false;
    }

    return !this.desktopSidebarPinnedExpanded && !this.desktopSidebarHoverExpanded;
  }

  get assistantSideWidthCss(): string {
    return `${this.assistantLayout.sidePanelWidth}px`;
  }

  get assistantBottomHeightCss(): string {
    return `${this.assistantLayout.bottomPanelHeight}px`;
  }

  get assistantSideWidthMaximum(): number {
    return typeof window === 'undefined' ? 672 : Math.max(352, Math.min(672, Math.round(window.innerWidth * 0.42)));
  }

  get assistantBottomHeightMaximum(): number {
    return typeof window === 'undefined' ? 672 : Math.max(240, Math.min(672, Math.round(window.innerHeight * 0.6)));
  }

  toggleSidebar(): void {
    if (this.isCompactMode) {
      if (!this.mobileSidebarOpen) {
        this.rightPanelOpen = false;
      }
      this.mobileSidebarOpen = !this.mobileSidebarOpen;
      return;
    }

    this.desktopSidebarVisible = !this.desktopSidebarVisible;
    this.persistDesktopSidebarVisibility(this.desktopSidebarVisible ? 'visible' : 'hidden');
  }

  toggleSidebarPinMode(): void {
    if (this.isCompactMode) {
      return;
    }

    this.clearSidebarHoverExpandTimer();
    this.desktopSidebarHoverExpanded = false;
    this.desktopSidebarPinnedExpanded = !this.desktopSidebarPinnedExpanded;
    this.persistDesktopSidebarPinMode(this.desktopSidebarPinnedExpanded ? 'expanded' : 'collapsed');
  }

  expandSidebarOnHover(): void {
    if (this.isCompactMode || this.desktopSidebarPinnedExpanded || this.desktopSidebarHoverExpanded || this.sidebarHoverExpandTimer) {
      return;
    }

    this.sidebarHoverExpandTimer = setTimeout(() => {
      this.sidebarHoverExpandTimer = null;
      if (!this.isCompactMode && !this.desktopSidebarPinnedExpanded) {
        this.desktopSidebarHoverExpanded = true;
        this.changeDetectorRef.markForCheck();
      }
    }, 250);
  }

  collapseSidebarAfterHover(): void {
    this.clearSidebarHoverExpandTimer();
    this.desktopSidebarHoverExpanded = false;
  }

  beginAssistantResize(event: PointerEvent, direction: AssistantResizeDirection): void {
    if (event.button !== 0 || this.isCompactMode) return;
    event.preventDefault();
    this.clearAssistantResize();

    const target = event.currentTarget as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
    const startCoordinate = direction === 'side' ? event.clientX : event.clientY;
    const startSize = direction === 'side' ? this.assistantLayout.sidePanelWidth : this.assistantLayout.bottomPanelHeight;
    const resize = (moveEvent: PointerEvent) => {
      const delta = direction === 'side'
        ? startCoordinate - moveEvent.clientX
        : startCoordinate - moveEvent.clientY;
      this.setAssistantPanelSize(direction, startSize + delta);
    };
    const finish = () => this.clearAssistantResize();
    document.addEventListener('pointermove', resize);
    document.addEventListener('pointerup', finish, { once: true });
    document.addEventListener('pointercancel', finish, { once: true });
    this.assistantResizeCleanup = () => {
      document.removeEventListener('pointermove', resize);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      if (target?.hasPointerCapture?.(event.pointerId)) target.releasePointerCapture(event.pointerId);
    };
  }

  resizeAssistantWithKeyboard(event: KeyboardEvent, direction: AssistantResizeDirection): void {
    const increment = event.shiftKey ? 48 : 16;
    const shouldGrow = direction === 'side' ? event.key === 'ArrowLeft' : event.key === 'ArrowUp';
    const shouldShrink = direction === 'side' ? event.key === 'ArrowRight' : event.key === 'ArrowDown';
    if (!shouldGrow && !shouldShrink) return;
    event.preventDefault();
    const current = direction === 'side' ? this.assistantLayout.sidePanelWidth : this.assistantLayout.bottomPanelHeight;
    this.setAssistantPanelSize(direction, current + (shouldGrow ? increment : -increment));
  }

  private setAssistantPanelSize(direction: AssistantResizeDirection, size: number): void {
    if (direction === 'side') {
      this.assistantUiStateService.setSidePanelWidth(Math.min(this.assistantSideWidthMaximum, size));
    } else {
      this.assistantUiStateService.setBottomPanelHeight(Math.min(this.assistantBottomHeightMaximum, size));
    }
  }

  private clearAssistantResize(): void {
    this.assistantResizeCleanup?.();
    this.assistantResizeCleanup = null;
  }

  private clearSidebarHoverExpandTimer(): void {
    if (this.sidebarHoverExpandTimer) {
      clearTimeout(this.sidebarHoverExpandTimer);
      this.sidebarHoverExpandTimer = null;
    }
  }

  closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  toggleRightPanel(): void {
    if (!this.routeHasRightPanel) {
      return;
    }

    if (this.isCompactMode && !this.rightPanelOpen) {
      this.mobileSidebarOpen = false;
    }

    this.rightPanelOpen = !this.rightPanelOpen;
    this.persistRightPanelOpenState();
  }

  closeRightPanel(): void {
    this.rightPanelOpen = false;
    this.persistRightPanelOpenState();
  }

  private restoreDesktopSidebarPinMode(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const savedMode = window.localStorage.getItem(this.desktopSidebarPinStorageKey);
    if (savedMode === 'collapsed') {
      this.desktopSidebarPinnedExpanded = false;
      return;
    }

    this.desktopSidebarPinnedExpanded = true;
    this.persistDesktopSidebarPinMode('expanded');
  }

  private persistDesktopSidebarPinMode(mode: 'expanded' | 'collapsed'): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.desktopSidebarPinStorageKey, mode);
  }

  private restoreDesktopSidebarVisibility(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const savedVisibility = window.localStorage.getItem(this.desktopSidebarVisibilityStorageKey);
    if (savedVisibility === 'hidden') {
      this.desktopSidebarVisible = false;
      return;
    }

    this.desktopSidebarVisible = true;
    this.persistDesktopSidebarVisibility('visible');
  }

  private persistDesktopSidebarVisibility(visibility: 'visible' | 'hidden'): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.desktopSidebarVisibilityStorageKey, visibility);
  }

  private resolveRightPanelStorageKey(): string | null {
    if (!this.currentConfig.rightPanel) {
      return null;
    }

    const rightPanelIdentity = this.currentConfig.rightPanel.kind || this.router.url.split('?')[0] || 'default';
    return `${this.rightPanelStateStoragePrefix}:${rightPanelIdentity}`;
  }

  private restoreRightPanelOpenState(): boolean {
    if (typeof window === 'undefined') {
      return true;
    }

    const storageKey = this.resolveRightPanelStorageKey();
    if (!storageKey) {
      return false;
    }

    const storedValue = window.localStorage.getItem(storageKey);
    if (storedValue === null) {
      return true;
    }

    return storedValue === 'true';
  }

  private persistRightPanelOpenState(): void {
    if (typeof window === 'undefined' || this.isCompactMode) {
      return;
    }

    const storageKey = this.resolveRightPanelStorageKey();
    if (!storageKey) {
      return;
    }

    window.localStorage.setItem(storageKey, String(this.rightPanelOpen));
  }

  private restoreAssistantLayoutFromUrl(): void {
    const queryParams = this.router.parseUrl(this.router.url).queryParams;
    if (queryParams['assistant'] !== 'open') {
      return;
    }

    const requestedMode = queryParams['assistantMode'];
    if (requestedMode === 'floating' || requestedMode === 'side' || requestedMode === 'bottom') {
      this.assistantUiStateService.setDisplayMode(requestedMode);
    }
    this.assistantUiStateService.setOpen(true);
  }

  private syncAssistantLayoutToUrl(layout: AssistantLayoutState): void {
    const queryParams = {
      assistant: layout.isOpen ? 'open' : null,
      assistantMode: layout.isOpen ? layout.displayMode : null
    };
    const target = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge'
    });
    if (this.router.serializeUrl(target) === this.router.url) {
      return;
    }

    void this.router.navigateByUrl(target, { replaceUrl: true });
  }

  private resolveShellConfig(): AppShellPageConfig {
    let activeRoute: ActivatedRoute | null = this.route;
    const shellConfigs: Partial<AppShellPageConfig>[] = [];

    while (activeRoute) {
      const shellConfig = activeRoute.snapshot.data['shell'] as Partial<AppShellPageConfig> | undefined;
      if (shellConfig) {
        shellConfigs.push(shellConfig);
      }

      activeRoute = activeRoute.firstChild;
    }

    return shellConfigs.reduce<AppShellPageConfig>(
      (config, shellConfig) => ({
        ...config,
        ...shellConfig
      }),
      { ...DEFAULT_APP_SHELL_CONFIG }
    );
  }
}
